/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {LocationStrategy} from '@angular/common';
import {Attribute, Directive, ElementRef, HostBinding, HostListener, Input, OnChanges, OnDestroy, Renderer2, isDevMode} from '@angular/core';
import {Subscription} from 'rxjs/Subscription';

import {QueryParamsHandling} from '../config';
import {NavigationEnd} from '../events';
import {Router} from '../router';
import {ActivatedRoute} from '../router_state';
import {UrlTree} from '../url_tree';
/**
 * \@whatItDoes Lets you link to specific parts of your app.
 * 
 * \@howToUse 
 * 
 * Consider the following route configuration:
 * `[{ path: 'user/:name', component: UserCmp }]`
 * 
 * When linking to this `user/:name` route, you can write:
 * `<a routerLink='/user/bob'>link to user component</a>`
 * 
 * \@description 
 * 
 * The RouterLink directives let you link to specific parts of your app.
 * 
 * When the link is static, you can use the directive as follows:
 * `<a routerLink="/user/bob">link to user component</a>`
 * 
 * If you use dynamic values to generate the link, you can pass an array of path
 * segments, followed by the params for each segment.
 * 
 * For instance `['/team', teamId, 'user', userName, {details: true}]`
 * means that we want to generate a link to `/team/11/user/bob;details=true`.
 * 
 * Multiple static segments can be merged into one
 * (e.g., `['/team/11/user', userName, {details: true}]`).
 * 
 * The first segment name can be prepended with `/`, `./`, or `../`:
 * * If the first segment begins with `/`, the router will look up the route from the root of the
 *   app.
 * * If the first segment begins with `./`, or doesn't begin with a slash, the router will
 *   instead look in the children of the current activated route.
 * * And if the first segment begins with `../`, the router will go up one level.
 * 
 * You can set query params and fragment as follows:
 * 
 * ```
 * <a [routerLink]="['/user/bob']" [queryParams]="{debug: true}" fragment="education">
 *   link to user component
 * </a>
 * ```
 * RouterLink will use these to generate this link: `/user/bob#education?debug=true`.
 * 
 * (Deprecated in v4.0.0 use `queryParamsHandling` instead) You can also tell the
 * directive to preserve the current query params and fragment:
 * 
 * ```
 * <a [routerLink]="['/user/bob']" preserveQueryParams preserveFragment>
 *   link to user component
 * </a>
 * ```
 * 
 * You can tell the directive to how to handle queryParams, available options are:
 *  - 'merge' merge the queryParams into the current queryParams
 *  - 'preserve' preserve the current queryParams
 *  - default / '' use the queryParams only
 *  same options for {\@link NavigationExtras#queryParamsHandling}
 * 
 * ```
 * <a [routerLink]="['/user/bob']" [queryParams]="{debug: true}" queryParamsHandling="merge">
 *   link to user component
 * </a>
 * ```
 * 
 * The router link directive always treats the provided input as a delta to the current url.
 * 
 * For instance, if the current url is `/user/(box//aux:team)`.
 * 
 * Then the following link `<a [routerLink]="['/user/jim']">Jim</a>` will generate the link
 * `/user/(jim//aux:team)`.
 * 
 * \@ngModule RouterModule
 * 
 * See {\@link Router#createUrlTree} for more information.
 * 
 * \@stable
 */
export class RouterLink {
   queryParams: {[k: string]: any};
   fragment: string;
   queryParamsHandling: QueryParamsHandling;
   preserveFragment: boolean;
   skipLocationChange: boolean;
   replaceUrl: boolean;
private commands: any[] = [];
private preserve: boolean;
/**
 * @param {?} router
 * @param {?} route
 * @param {?} tabIndex
 * @param {?} renderer
 * @param {?} el
 */
constructor(
private router: Router,
private route: ActivatedRoute,
       tabIndex: string, renderer: Renderer2, el: ElementRef) {
    if (tabIndex == null) {
      renderer.setAttribute(el.nativeElement, 'tabindex', '0');
    }
  }
/**
 * @param {?} commands
 * @return {?}
 */
set routerLink(commands: any[]|string) {
    if (commands != null) {
      this.commands = Array.isArray(commands) ? commands : [commands];
    } else {
      this.commands = [];
    }
  }
/**
 * @deprecated 4.0.0 use `queryParamsHandling` instead.
 * @param {?} value
 * @return {?}
 */
set preserveQueryParams(value: boolean) {
    if (isDevMode() && /** @type {?} */(( <any>console)) && /** @type {?} */(( <any>console.warn))) {
      console.warn('preserveQueryParams is deprecated!, use queryParamsHandling instead.');
    }
    this.preserve = value;
  }
/**
 * @return {?}
 */
onClick(): boolean {
    const /** @type {?} */ extras = {
      skipLocationChange: attrBoolValue(this.skipLocationChange),
      replaceUrl: attrBoolValue(this.replaceUrl),
    };
    this.router.navigateByUrl(this.urlTree, extras);
    return true;
  }
/**
 * @return {?}
 */
get urlTree(): UrlTree {
    return this.router.createUrlTree(this.commands, {
      relativeTo: this.route,
      queryParams: this.queryParams,
      fragment: this.fragment,
      preserveQueryParams: attrBoolValue(this.preserve),
      queryParamsHandling: this.queryParamsHandling,
      preserveFragment: attrBoolValue(this.preserveFragment),
    });
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{selector: ':not(a)[routerLink]'}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Router, },
{type: ActivatedRoute, },
{type: undefined, decorators: [{ type: Attribute, args: ['tabindex', ] }, ]},
{type: Renderer2, },
{type: ElementRef, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'queryParams': [{ type: Input },],
'fragment': [{ type: Input },],
'queryParamsHandling': [{ type: Input },],
'preserveFragment': [{ type: Input },],
'skipLocationChange': [{ type: Input },],
'replaceUrl': [{ type: Input },],
'routerLink': [{ type: Input },],
'preserveQueryParams': [{ type: Input },],
'onClick': [{ type: HostListener, args: ['click', ] },],
};
}

function RouterLink_tsickle_Closure_declarations() {
/** @type {?} */
RouterLink.decorators;
/**
 * @nocollapse
 * @type {?}
 */
RouterLink.ctorParameters;
/** @type {?} */
RouterLink.propDecorators;
/** @type {?} */
RouterLink.prototype.queryParams;
/** @type {?} */
RouterLink.prototype.fragment;
/** @type {?} */
RouterLink.prototype.queryParamsHandling;
/** @type {?} */
RouterLink.prototype.preserveFragment;
/** @type {?} */
RouterLink.prototype.skipLocationChange;
/** @type {?} */
RouterLink.prototype.replaceUrl;
/** @type {?} */
RouterLink.prototype.commands;
/** @type {?} */
RouterLink.prototype.preserve;
/** @type {?} */
RouterLink.prototype.router;
/** @type {?} */
RouterLink.prototype.route;
}

/**
 * \@whatItDoes Lets you link to specific parts of your app.
 * 
 * See {\@link RouterLink} for more information.
 * 
 * \@ngModule RouterModule
 * 
 * \@stable
 */
export class RouterLinkWithHref implements OnChanges, OnDestroy {
    target: string;
   queryParams: {[k: string]: any};
   fragment: string;
   queryParamsHandling: QueryParamsHandling;
   preserveFragment: boolean;
   skipLocationChange: boolean;
   replaceUrl: boolean;
private commands: any[] = [];
private subscription: Subscription;
private preserve: boolean;

  // the url displayed on the anchor element.
   href: string;
/**
 * @param {?} router
 * @param {?} route
 * @param {?} locationStrategy
 */
constructor(
private router: Router,
private route: ActivatedRoute,
private locationStrategy: LocationStrategy) {
    this.subscription = router.events.subscribe(s => {
      if (s instanceof NavigationEnd) {
        this.updateTargetUrlAndHref();
      }
    });
  }
/**
 * @param {?} commands
 * @return {?}
 */
set routerLink(commands: any[]|string) {
    if (commands != null) {
      this.commands = Array.isArray(commands) ? commands : [commands];
    } else {
      this.commands = [];
    }
  }
/**
 * @param {?} value
 * @return {?}
 */
set preserveQueryParams(value: boolean) {
    if (isDevMode() && /** @type {?} */(( <any>console)) && /** @type {?} */(( <any>console.warn))) {
      console.warn('preserveQueryParams is deprecated, use queryParamsHandling instead.');
    }
    this.preserve = value;
  }
/**
 * @param {?} changes
 * @return {?}
 */
ngOnChanges(changes: {}): any { this.updateTargetUrlAndHref(); }
/**
 * @return {?}
 */
ngOnDestroy(): any { this.subscription.unsubscribe(); }
/**
 * @param {?} button
 * @param {?} ctrlKey
 * @param {?} metaKey
 * @param {?} shiftKey
 * @return {?}
 */
onClick(button: number, ctrlKey: boolean, metaKey: boolean, shiftKey: boolean): boolean {
    if (button !== 0 || ctrlKey || metaKey || shiftKey) {
      return true;
    }

    if (typeof this.target === 'string' && this.target != '_self') {
      return true;
    }

    const /** @type {?} */ extras = {
      skipLocationChange: attrBoolValue(this.skipLocationChange),
      replaceUrl: attrBoolValue(this.replaceUrl),
    };
    this.router.navigateByUrl(this.urlTree, extras);
    return false;
  }
/**
 * @return {?}
 */
private updateTargetUrlAndHref(): void {
    this.href = this.locationStrategy.prepareExternalUrl(this.router.serializeUrl(this.urlTree));
  }
/**
 * @return {?}
 */
get urlTree(): UrlTree {
    return this.router.createUrlTree(this.commands, {
      relativeTo: this.route,
      queryParams: this.queryParams,
      fragment: this.fragment,
      preserveQueryParams: attrBoolValue(this.preserve),
      queryParamsHandling: this.queryParamsHandling,
      preserveFragment: attrBoolValue(this.preserveFragment),
    });
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{selector: 'a[routerLink]'}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Router, },
{type: ActivatedRoute, },
{type: LocationStrategy, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'target': [{ type: HostBinding, args: ['attr.target', ] },{ type: Input },],
'queryParams': [{ type: Input },],
'fragment': [{ type: Input },],
'queryParamsHandling': [{ type: Input },],
'preserveFragment': [{ type: Input },],
'skipLocationChange': [{ type: Input },],
'replaceUrl': [{ type: Input },],
'href': [{ type: HostBinding },],
'routerLink': [{ type: Input },],
'preserveQueryParams': [{ type: Input },],
'onClick': [{ type: HostListener, args: ['click', ['$event.button', '$event.ctrlKey', '$event.metaKey', '$event.shiftKey'], ] },],
};
}

function RouterLinkWithHref_tsickle_Closure_declarations() {
/** @type {?} */
RouterLinkWithHref.decorators;
/**
 * @nocollapse
 * @type {?}
 */
RouterLinkWithHref.ctorParameters;
/** @type {?} */
RouterLinkWithHref.propDecorators;
/** @type {?} */
RouterLinkWithHref.prototype.target;
/** @type {?} */
RouterLinkWithHref.prototype.queryParams;
/** @type {?} */
RouterLinkWithHref.prototype.fragment;
/** @type {?} */
RouterLinkWithHref.prototype.queryParamsHandling;
/** @type {?} */
RouterLinkWithHref.prototype.preserveFragment;
/** @type {?} */
RouterLinkWithHref.prototype.skipLocationChange;
/** @type {?} */
RouterLinkWithHref.prototype.replaceUrl;
/** @type {?} */
RouterLinkWithHref.prototype.commands;
/** @type {?} */
RouterLinkWithHref.prototype.subscription;
/** @type {?} */
RouterLinkWithHref.prototype.preserve;
/** @type {?} */
RouterLinkWithHref.prototype.href;
/** @type {?} */
RouterLinkWithHref.prototype.router;
/** @type {?} */
RouterLinkWithHref.prototype.route;
/** @type {?} */
RouterLinkWithHref.prototype.locationStrategy;
}

/**
 * @param {?} s
 * @return {?}
 */
function attrBoolValue(s: any): boolean {
  return s === '' || !!s;
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}



// WEBPACK FOOTER //
// ../../../../packages/router/src/directives/router_link.ts